import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ConfigEntityRow } from '../api';
import type { Session } from '../App';
import { Container, PageHeader } from '../App';
import SchemaForm from '../components/SchemaForm';
import { getEntityHelp } from '../data/entityHelp';

interface Props { session: Session; }
interface SchemaEntry { package: string; entity_type: string; }

export default function Explorer({ session }: Props) {
  const [allRows, setAllRows] = useState<ConfigEntityRow[]>([]);
  const [schemas, setSchemas] = useState<SchemaEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [activePackage, setActivePackage] = useState<string | null>(null);

  // Create-entity state
  const [creating, setCreating] = useState<SchemaEntry | null>(null);
  const [schema, setSchema] = useState<Record<string, unknown> | null>(null);
  const [entityKey, setEntityKey] = useState('');
  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [rawJson, setRawJson] = useState('{}');
  const [useForm, setUseForm] = useState(true);

  const reload = () => {
    api.listEntities(session.gameId).then(setAllRows).catch(e => setError(e.message));
  };
  useEffect(reload, [session.gameId]);
  useEffect(() => { api.listSchemas().then(setSchemas).catch(() => {}); }, []);

  // Close create panel when switching tabs
  const switchPackage = (pkg: string | null) => {
    setActivePackage(pkg);
    setCreating(null);
  };

  // Derive unique packages from schemas
  const packages = [...new Set(schemas.map(s => s.package))];

  // Filter rows + schemas for the active package
  const filteredRows = activePackage
    ? allRows.filter(r => r.package === activePackage)
    : allRows;
  const filteredSchemas = activePackage
    ? schemas.filter(s => s.package === activePackage)
    : schemas;

  // Group rows by entity_type
  const grouped = new Map<string, ConfigEntityRow[]>();
  for (const r of filteredRows) {
    const key = `${r.package}/${r.entity_type}`;
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key)!.push(r);
  }
  // Also ensure all schema types show (even empty)
  for (const s of filteredSchemas) {
    const key = `${s.package}/${s.entity_type}`;
    if (!grouped.has(key)) grouped.set(key, []);
  }

  const startCreate = async (entry: SchemaEntry) => {
    setCreating(entry);
    setEntityKey('');
    setFormData({});
    setRawJson('{}');
    setSchema(null);
    try {
      const s = await api.getSchema(entry.package, entry.entity_type);
      setSchema(s);
    } catch { /* no schema — fallback */ }
  };

  const handleCreate = async () => {
    if (!creating) return;
    setError(null);
    try {
      const data = useForm && schema ? formData : JSON.parse(rawJson);
      await api.createEntity(session.gameId, {
        package: creating.package,
        entity_type: creating.entity_type,
        entity_key: entityKey,
        data,
        user_id: session.userId,
      });
      setCreating(null);
      reload();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <Container>
      <PageHeader
        title="Config Explorer"
        subtitle="This is where durable game configuration is authored. New records start as drafts."
      />

      {error && <div className="error-banner">{error}</div>}

      <div className="inline-help mb-4">
        Pick a package, choose an entity type, then create a draft. Publish from Drafts &amp; Publish when the set is ready.
      </div>

      {/* Package tab bar */}
      <div className="flex items-center gap-2 mb-5 flex-wrap">
        <button
          className={`btn ${activePackage === null ? 'btn-primary' : ''}`}
          onClick={() => switchPackage(null)}
        >
          All packages
        </button>
        {packages.map(pkg => (
          <button
            key={pkg}
            className={`btn ${activePackage === pkg ? 'btn-primary' : ''}`}
            onClick={() => switchPackage(pkg)}
          >
            {humanize(pkg)}
          </button>
        ))}
        {packages.length === 0 && (
          <span className="text-xs text-fg-muted">No packages registered.</span>
        )}
      </div>

      {/* Create panel (slides in when a type's +New is clicked) */}
      {creating && (
        <div className="card mb-5 border-brand/40">
          <div className="flex items-center justify-between mb-4">
            <h3 className="m-0 text-sm font-semibold">
              New {humanize(creating.entity_type)}{' '}
              <span className="text-fg-dim font-normal">in {creating.package}</span>
            </h3>
            <div className="flex items-center gap-3">
              {schema && (
                <label className="flex items-center gap-2 text-xs text-fg-dim cursor-pointer">
                  <input
                    type="checkbox"
                    checked={useForm}
                    onChange={e => setUseForm(e.target.checked)}
                    className="accent-brand"
                  />
                  Form view
                </label>
              )}
              <button className="btn" onClick={() => setCreating(null)}>Cancel</button>
            </div>
          </div>

          {/* Help panel */}
          {(() => {
            const help = getEntityHelp(creating.package, creating.entity_type);
            if (!help) return null;
            return (
              <div className="mb-4 border border-line rounded-lg bg-bg-1 p-4">
                <div className="text-sm text-fg-2 mb-2">{help.description}</div>
                <div className="text-xs text-fg-dim mb-3">
                  <strong className="text-fg-dim uppercase tracking-wider text-[10.5px]">When to use:</strong>{' '}
                  {help.whenToUse}
                </div>
                <details>
                  <summary className="text-xs text-fg-muted cursor-pointer hover:text-fg-dim">
                    Show example config
                  </summary>
                  <pre className="mt-2 bg-bg-input border border-line rounded p-3 text-[12px] text-fg-2 overflow-x-auto">
                    {JSON.stringify(help.example, null, 2)}
                  </pre>
                  <button
                    type="button"
                    className="btn mt-2 text-xs"
                    onClick={() => {
                      setUseForm(false);
                      setRawJson(JSON.stringify(help.example, null, 2));
                    }}
                  >
                    Use this example
                  </button>
                </details>
              </div>
            );
          })()}

          <div className="mb-4">
            <label className="block text-[11.5px] uppercase tracking-wider font-medium text-fg-dim mb-1.5">
              Entity key
            </label>
            <input
              className="input"
              placeholder="e.g. gold, iron_sword, starter_pack…"
              value={entityKey}
              onChange={e => setEntityKey(e.target.value)}
              autoFocus
            />
          </div>

          {schema && useForm ? (
            <div className="border border-line rounded-lg p-4 bg-bg-1 mb-4">
              <SchemaForm
                schema={schema as any}
                value={formData}
                onChange={setFormData}
              />
            </div>
          ) : (
            <textarea
              className="input font-mono mb-4"
              value={rawJson}
              onChange={e => setRawJson(e.target.value)}
              style={{ minHeight: 200 }}
            />
          )}

          <div className="flex justify-end">
            <button
              className="btn btn-primary"
              onClick={handleCreate}
              disabled={!entityKey.trim()}
            >
              Create draft
            </button>
          </div>
        </div>
      )}

      {/* Entity type sections */}
      {[...grouped.entries()].map(([key, rows]) => {
        const [pkg, entityType] = key.split('/');
        return (
          <EntityTypeSection
            key={key}
            package_={pkg}
            entityType={entityType}
            rows={rows}
            onNew={() => startCreate({ package: pkg, entity_type: entityType })}
            showPackage={activePackage === null}
          />
        );
      })}

      {grouped.size === 0 && (
        <div className="empty">
          <div className="text-fg-2 font-medium mb-1">No entity types</div>
          <div className="text-xs text-fg-muted">
            {activePackage
              ? `No types registered for "${activePackage}".`
              : 'No schemas registered for this game. Create a game with packages enabled, or switch games from the sidebar.'}
          </div>
        </div>
      )}
    </Container>
  );
}

function EntityTypeSection({
  package_,
  entityType,
  rows,
  onNew,
  showPackage,
}: {
  package_: string;
  entityType: string;
  rows: ConfigEntityRow[];
  onNew: () => void;
  showPackage: boolean;
}) {
  const help = getEntityHelp(package_, entityType);
  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-[13px] font-semibold uppercase tracking-[0.06em] text-fg-dim m-0 flex items-center gap-2">
          {showPackage && (
            <span className="badge badge-muted text-[10px]">{package_}</span>
          )}
          {humanize(entityType)}
          <span className="text-fg-muted font-normal normal-case tracking-normal text-xs">
            ({rows.length})
          </span>
        </h2>
        <button className="btn" onClick={onNew}>
          ＋ New
        </button>
      </div>
      {help && (
        <div className="text-xs text-fg-dim mb-2">{help.description}</div>
      )}

      {rows.length === 0 ? (
        <div className="empty py-8">
          <div className="text-fg-2 text-sm mb-1">No {humanize(entityType).toLowerCase()} entities yet</div>
          <div className="text-xs text-fg-muted">Create one here; it will appear in Drafts &amp; Publish before going live.</div>
        </div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Key</th>
                <th>Status</th>
                <th>Version</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={`${r.id}-${r.status}`}>
                  <td>
                    <Link to={`/entities/${r.id}`} className="font-medium">
                      {r.entity_key}
                    </Link>
                  </td>
                  <td>
                    <span className={`badge badge-${r.status}`}>{r.status}</span>
                  </td>
                  <td className="text-fg-dim font-mono text-[12px]">v{r.version}</td>
                  <td className="text-fg-muted text-xs">
                    {new Date(r.updated_at).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function humanize(s: string): string {
  return s
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, c => c.toUpperCase());
}
